import {useMutation} from '@tanstack/react-query'
import type {SuggestAction, SuggestLocation, SuggestObject, SuggestResponse} from '@yandex-delivery/api/model/suggest'
import {type LngLat, type SuggestResponse as SuggestResponseBase, useAddNotification} from '@yandex-delivery/blocks'
import {DEFAULT_CENTER} from '@yandex-delivery/blocks/map/consts'
import type {ServerError} from '@yandex-delivery/core/utils/network'
import {useCallback} from 'react'
import {useFormContext} from 'react-hook-form'

import {toUserAddressPin} from '../../../api/address/services/addressService/mappers'
import {useService} from '../../../api/address/suggest'
import {getAddressDetails} from '../../../lib/get-address-details'
import {getAddressCoordinatesFieldName} from '../utils/get-address-coordinates-field-name'
import {getAddressFieldName} from '../utils/get-address-field-name'

export function useFetchAddressByLocation(location: LngLat = DEFAULT_CENTER) {
  const addNotification = useAddNotification()
  const {fetchAddressSuggest} = useService()
  const {setValue, watch} = useFormContext()

  const lngLatToSuggestLocation = (locationCoords: LngLat): SuggestLocation => {
    return [locationCoords[0], locationCoords[1]]
  }

  const mutation = useMutation<
    SuggestResponseBase<SuggestResponse, SuggestObject>,
    ServerError,
    {coordinates: LngLat; index: number}
  >({
    mutationFn: ({coordinates, index}) =>
      fetchAddressSuggest({
        location: lngLatToSuggestLocation(location),
        action: 'pin_drop' as SuggestAction,
        point: lngLatToSuggestLocation(coordinates),
        type: index === 0 ? 'a' : 'b',
      }),
    onSuccess: (result, {coordinates, index}) => {
      const fieldCoordinatesPath = getAddressCoordinatesFieldName(index)
      setValue(fieldCoordinatesPath, coordinates)

      const address = toUserAddressPin(result?.suggest[0]?.__origin)

      const fieldPath = getAddressFieldName(index)
      const prevAddress = watch(fieldPath)
      const details = prevAddress ? getAddressDetails(prevAddress) : {}
      setValue(fieldPath, {...details, ...address})
    },
    onError: (error, {index}) => {
      addNotification(error)
      const fieldCoordinatesPath = getAddressCoordinatesFieldName(index)
      setValue(fieldCoordinatesPath, undefined)
    },
  })

  const fetchTaxiSuggestHandler = useCallback(
    (coordinates: LngLat, index: number) => {
      mutation.mutate({coordinates, index})
    },
    [mutation],
  )

  return {
    isPending: mutation.isPending,
    suggestValue: mutation.data,
    fetchTaxiSuggestHandler,
  }
}
